import { useState, type SyntheticEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { MdLock, MdMail, MdKey, MdDirectionsBike } from 'react-icons/md';
import { initiatePasswordReset, confirmPasswordResetFlow } from '@/services/auth.service';

type Step = 'request' | 'confirm';

function mapResetError(err: unknown): string {
  const name = (err as { name?: string })?.name ?? '';
  switch (name) {
    case 'CodeMismatchException':
      return 'Código inválido. Verifique o código recebido por e-mail.';
    case 'ExpiredCodeException':
      return 'Código expirado. Solicite um novo.';
    case 'InvalidPasswordException':
      return 'Senha não atende aos requisitos mínimos.';
    case 'LimitExceededException':
      return 'Muitas tentativas. Tente novamente em alguns minutos.';
    default:
      return 'Não foi possível redefinir a senha. Tente novamente.';
  }
}

export default function EsqueciSenhaPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  if (isAuthenticated) {
    return <Navigate to="/perfil" replace />;
  }

  const handleRequestSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    if (!email) return;

    let success = false;
    try {
      setIsLoading(true);
      await initiatePasswordReset(email);
      success = true;
    } catch (err: unknown) {
      const name = (err as { name?: string })?.name ?? '';
      if (name === 'LimitExceededException') {
        setError('Muitas tentativas. Tente novamente em alguns minutos.');
      } else if (name === 'UserNotFoundException' || name === 'InvalidParameterException') {
        success = true;
      } else {
        setError('Não foi possível enviar o código. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }

    if (success) {
      setStep('confirm');
      setInfo(`Enviamos um código para ${email}`);
    }
  };

  const handleConfirmSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    setError('');

    if (!/^\d{6}$/.test(code)) {
      setError('O código deve ter exatamente 6 dígitos numéricos.');
      return;
    }
    if (newPassword.length < 8) {
      setError('A senha deve ter pelo menos 8 caracteres.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }

    try {
      setIsLoading(true);
      await confirmPasswordResetFlow({ username: email, confirmationCode: code, newPassword });
      navigate('/login', { state: { resetSuccess: true } });
    } catch (err) {
      setError(mapResetError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setInfo('');
    try {
      setIsLoading(true);
      await initiatePasswordReset(email);
      setInfo(`Código reenviado para ${email}`);
    } catch (err: unknown) {
      const name = (err as { name?: string })?.name ?? '';
      if (name === 'LimitExceededException') {
        setError('Muitas tentativas. Tente novamente em alguns minutos.');
      } else {
        setError('Não foi possível reenviar o código. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-600 to-brand-800 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-100 rounded-full mb-4">
              <MdDirectionsBike className="text-3xl text-brand-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">RandoBR</h1>
            <p className="text-gray-600">
              {step === 'request' ? 'Recuperação de senha' : 'Definir nova senha'}
            </p>
          </div>

          {info && (
            <div className="mb-4 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm">
              {info}
            </div>
          )}

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {step === 'request' ? (
            <form onSubmit={handleRequestSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">E-mail</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MdMail className="text-gray-400 text-xl" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-field pl-10"
                    placeholder="Digite seu e-mail"
                    disabled={isLoading}
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              <button type="submit" disabled={isLoading} className="btn-primary w-full flex items-center justify-center gap-2">
                {isLoading ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span>Enviando...</span>
                  </>
                ) : (
                  <span>Enviar código</span>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleConfirmSubmit} className="space-y-6">
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">Código de verificação</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MdKey className="text-gray-400 text-xl" />
                  </div>
                  <input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replaceAll(/\D/g, ''))}
                    className="input-field pl-10"
                    placeholder="6 dígitos"
                    disabled={isLoading}
                    autoComplete="one-time-code"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">Nova senha</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MdLock className="text-gray-400 text-xl" />
                  </div>
                  <input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="input-field pl-10"
                    placeholder="Mínimo 8 caracteres"
                    disabled={isLoading}
                    autoComplete="new-password"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">Confirmar nova senha</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MdLock className="text-gray-400 text-xl" />
                  </div>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input-field pl-10"
                    placeholder="Repita a nova senha"
                    disabled={isLoading}
                    autoComplete="new-password"
                    required
                  />
                </div>
              </div>

              <button type="submit" disabled={isLoading} className="btn-primary w-full flex items-center justify-center gap-2">
                {isLoading ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span>Redefinindo...</span>
                  </>
                ) : (
                  <span>Redefinir senha</span>
                )}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={isLoading}
                  className="text-sm text-brand-600 hover:text-brand-700 font-medium disabled:opacity-50"
                >
                  Reenviar código
                </button>
              </div>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-gray-500 hover:text-gray-700 font-medium">
              Voltar para login
            </Link>
          </div>
        </div>

        <p className="text-center text-white text-sm mt-8">&copy; 2026 RandoBR. Todos os direitos reservados.</p>
      </div>
    </div>
  );
}
